import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { getCachedClients } from '@/lib/offlineDataCache';
import {
  useCreateFinanceEntry,
  useFinanceCategories,
  useUpdateFinanceEntry,
  type FinanceEntryInput,
} from '@/hooks/useFinance';
import { todayIsoDate } from '@/lib/financeStatus';
// Campo de DINHEIRO usa o parser de dinheiro (#71): `parseFloat(x.replace(',', '.'))`
// lia "1.500,00" como 1,5 (o parseFloat para no segundo ponto). O par
// parse/format anda junto — hidratar com o número cru do banco ("250.5") e
// reparsá-lo trataria o ponto decimal como milhar e inflaria o valor.
//
// Desde 2026-08-04 o campo é MASCARADO (`MoneyInput`, #157): o parser resolvia
// "1.500,00", mas não a ambiguidade de quem digita só "30.50" — em pt-BR o
// ponto é milhar (R$ 3.050) e no teclado do celular ele sai como decimal
// (R$ 30,50). Com máscara não há separador para digitar, então não há o que
// adivinhar. Por isso o estado guarda NÚMERO, não texto: o texto é assunto do
// componente, e um estado de string convidaria o próximo a parseá-lo à mão.
import { parseBRLInput } from '@/lib/currencyInput';
import { MoneyInput } from '@/components/ui/money-input';
import { useFeature } from '@/hooks/useFeature';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFinanceAttachment } from '@/hooks/useFinanceAttachment';
import {
  BILLABLE_HELP_TEXT,
  BILLABLE_REQUIRES_QUOTE_MESSAGE,
  notifyBillableExpensesUpdated,
} from '@/lib/billableExpense';
import {
  assertQuoteEditableForBillable,
  billableOfflineError,
  BillableExpenseError,
  syncBillableQuoteLine,
} from '@/lib/billableExpenseSync';
import { StorageImage } from '@/components/ui/StorageImage';
import { GenerateReceiptButton } from '@/components/receipts/GenerateReceiptButton';
import { PAYMENT_METHOD_LABELS, RECURRENCE_LABELS } from '@/lib/financeLabels';
import { useFinanceEntriesRaw } from '@/hooks/useFinance';
import type {
  FinanceEntryWithStatus,
  FinanceKind,
  PaymentMethod,
  Recurrence,
} from '@/types/database';

export interface FinanceEntryPrefill {
  kind?: FinanceKind;
  description?: string;
  amount?: number;
  client_id?: string | null;
  quote_id?: string | null;
  due_date?: string;
  payment_method?: PaymentMethod | null;
  notes?: string | null;
  /** Já abre com a despesa marcada como cobrável (entrada pelo orçamento). */
  billable?: boolean;
}

/**
 * O que os chamadores empurram para esta rota pelo STATE da navegação.
 *
 * Vai no state, não na URL: são objetos (o prefill) e um id cujo significado é
 * de AUTORIZAÇÃO (`ownerQuoteId` habilita marcar a despesa como cobrável) — pôr
 * isso em query string convidaria a forjar o dono na barra de endereço.
 */
export interface FinanceEntryRouteState {
  prefill?: FinanceEntryPrefill;
  defaultKind?: FinanceKind;
  /**
   * Id do orçamento que ABRIU este formulário. É o que habilita a marcação de
   * despesa cobrável: o ORÇAMENTO é o dono dela (ver `src/lib/billableExpense.ts`),
   * e marcar a partir de `/financeiro` faria um documento mudar sozinho.
   */
  ownerQuoteId?: string | null;
}

const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];
const RECURRENCES = Object.keys(RECURRENCE_LABELS) as Recurrence[];

export default function FinanceEntryFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const routeState = (location.state as FinanceEntryRouteState | null) ?? null;
  const prefill = routeState?.prefill;
  const defaultKind: FinanceKind = routeState?.defaultKind ?? 'income';
  const ownerQuoteId = routeState?.ownerQuoteId ?? null;

  /** Fechar = voltar para de onde veio (lista, orçamento ou o próprio lançamento). */
  const close = () => navigate(-1);

  // A entidade em edição vem da LISTA já cacheada (offline-first), do mesmo
  // jeito que OS e contrato: o formulário não faz consulta própria.
  const { entries, isLoading: loadingEntries } = useFinanceEntriesRaw();
  const entry = id ? (entries.find((e: any) => e.id === id) ?? null) : null;

  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { categories } = useFinanceCategories();
  const createEntry = useCreateFinanceEntry();
  const updateEntry = useUpdateFinanceEntry();
  const attachment = useFinanceAttachment();
  // Anexo é a mesma natureza das fotos do orçamento (mídia em storage), então
  // segue a MESMA flag — o financeiro ser grátis não abre anexo por herança
  // (CLAUDE.md #54).
  const attachmentFeature = useFeature('quote_photos');

  const isEdit = Boolean(entry);

  const [kind, setKind] = useState<FinanceKind>(defaultKind);
  const [description, setDescription] = useState('');
  // `null` = campo VAZIO, e não zero — é o que faz o placeholder aparecer num
  // lançamento novo (`numberToMaskedBRL(null)` devolve ''). Colapsar os dois em
  // 0 mostraria "0,00" num formulário em branco.
  const [amount, setAmount] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState(todayIsoDate());
  const [paid, setPaid] = useState(false);
  // Data do pagamento — editável (aceita retroativo). Sem isto todo pagamento
  // vira "hoje" e o Relatório de Pagamentos joga o valor no mês errado.
  const [paidDate, setPaidDate] = useState(todayIsoDate());
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [quoteId, setQuoteId] = useState<string>('');
  const [recurrence, setRecurrence] = useState<Recurrence>('none');
  const [notes, setNotes] = useState('');
  const [billable, setBillable] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);

  /**
   * Hidrata UMA vez. No diálogo o gatilho era `open` (e o dado já existia);
   * aqui a página nasce montada e o lançamento chega depois — sem o marcador,
   * cada refetch da lista reescreveria o que está sendo digitado.
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    // Edição: espera o lançamento chegar. ⚠️ Sabotar esta linha NÃO reprova a
    // suíte — no harness o mock devolve a entidade já na primeira renderização,
    // então o caso do dado atrasado (cache frio, link direto) só existe no app
    // real. Fica registrado para ninguém ler o verde como cobertura disso.
    if (id && !entry) return;
    hydratedRef.current = true;
    if (entry) {
      setKind(entry.kind);
      setDescription(entry.description ?? '');
      // `entry.amount` pode chegar como STRING do PostgREST ("250.50"); é por
      // isso que passa por `parseBRLInput`, que trata string sem vírgula como
      // número cru (#123a), em vez de `Number()` direto.
      setAmount(entry.amount == null ? null : parseBRLInput(entry.amount));
      setDueDate(entry.due_date ?? todayIsoDate());
      setPaid(entry.status === 'paid');
      setPaidDate(entry.paid_date ?? entry.due_date ?? todayIsoDate());
      setPaymentMethod((entry.payment_method as PaymentMethod) ?? '');
      setCategoryId(entry.category_id ?? '');
      setClientId(entry.client_id ?? '');
      setQuoteId(entry.quote_id ?? '');
      setRecurrence((entry.recurrence as Recurrence) ?? 'none');
      setNotes(entry.notes ?? '');
      setBillable(Boolean(entry.billable));
      setAttachmentUrl(entry.attachment_url ?? null);
      return;
    }
    setKind(prefill?.kind ?? defaultKind);
    setDescription(prefill?.description ?? '');
    setAmount(prefill?.amount ?? null);
    setDueDate(prefill?.due_date ?? todayIsoDate());
    setPaid(false);
    setPaidDate(todayIsoDate());
    setPaymentMethod(prefill?.payment_method ?? '');
    setCategoryId('');
    setClientId(prefill?.client_id ?? '');
    setQuoteId(prefill?.quote_id ?? '');
    setRecurrence('none');
    setNotes(prefill?.notes ?? '');
    setBillable(Boolean(prefill?.billable));
    setAttachmentUrl(null);
    // `prefill`/`defaultKind` vêm do state da navegação e são lidos na entrada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, entry]);

  /**
   * O orçamento DONO da despesa cobrável. Só existe quando o formulário foi
   * aberto a partir de um orçamento (ou quando já se está editando uma despesa
   * cobrável, que nasceu assim). Sem ele a marcação fica desabilitada: criar em
   * `/financeiro` e ver um orçamento mudar sozinho é ação à distância.
   */
  const billableQuoteId = ownerQuoteId ?? (entry?.billable ? entry.quote_id : null);
  const canMarkBillable = kind === 'expense' && Boolean(billableQuoteId);
  /** Marcar/desmarcar altera o total de um documento — precisa de rede. */
  const billableTouched = billable || Boolean(entry?.billable);

  // Trocar para ENTRADA desmarca: o banco recusa `billable` numa entrada (CHECK)
  // e, conceitualmente, uma entrada já É a cobrança.
  useEffect(() => {
    if (kind !== 'expense' && billable) setBillable(false);
  }, [kind, billable]);

  /**
   * Cobrável e RECORRENTE não combinam. `expandRecurrence` cria 12 lançamentos,
   * mas só UM vira linha do orçamento — os outros 11 ficariam marcados como
   * cobráveis sem nada os cobrando, e o usuário veria despesas "cobráveis"
   * fantasmas no financeiro. Um repasse é um gasto pontual daquele documento.
   */
  useEffect(() => {
    if (billable && recurrence !== 'none') setRecurrence('none');
  }, [billable, recurrence]);

  const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Limpa o input para permitir reanexar o MESMO arquivo depois de remover.
    event.target.value = '';
    if (!file) return;
    // `require()` abre o paywall e devolve false quando a feature é PRO.
    if (!attachmentFeature.require()) return;
    const url = await attachment.pickAttachment(file);
    if (url) setAttachmentUrl(url);
  };

  const { data: clients = [] } = useQuery({
    queryKey: ['finance-clients', activeCompany?.id],
    enabled: Boolean(user?.id && activeCompany?.id),
    networkMode: 'offlineFirst',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedClients(user.id) ?? undefined : undefined),
    queryFn: async () => {
      if (!user?.id || !activeCompany?.id) return [];
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', activeCompany.id)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Orçamentos da empresa — permitem vincular um lançamento avulso a um orçamento
  // (fora do fluxo do QuoteFinancePrompt, que já vem com quote_id no prefill).
  const { data: quotes = [] } = useQuery({
    queryKey: ['finance-quotes', activeCompany?.id],
    enabled: Boolean(user?.id && activeCompany?.id),
    networkMode: 'offlineFirst',
    queryFn: async () => {
      if (!user?.id || !activeCompany?.id) return [];
      const { data, error } = await supabase
        .from('quotes')
        .select('id, title, sequence_id, total, client_id')
        .eq('company_id', activeCompany.id)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const quoteLabel = (q: { title?: string | null; sequence_id?: number | null }) =>
    q.title?.trim()
      ? `${q.sequence_id ? `#${q.sequence_id} — ` : ''}${q.title}`
      : q.sequence_id
        ? `Orçamento #${q.sequence_id}`
        : 'Orçamento';

  const handleSelectQuote = (id: string) => {
    setQuoteId(id);
    if (!id) return;
    const q = quotes.find((item) => item.id === id);
    if (!q) return;
    // Auto-preenche o que ainda estiver vazio, sem sobrescrever o que o usuário digitou.
    if (!amount && q.total != null) setAmount(parseBRLInput(q.total));
    if (!clientId && q.client_id) setClientId(q.client_id);
    if (!description.trim()) setDescription(quoteLabel(q));
  };

  const categoryOptions = useMemo(
    () => categories.filter((c) => c.kind === kind),
    [categories, kind],
  );

  const submitting = createEntry.isPending || updateEntry.isPending;

  const handleSubmit = async () => {
    const parsedAmount = Math.round((amount ?? 0) * 100) / 100;
    if (!description.trim()) {
      toast.warning('Informe uma descrição.');
      return;
    }
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.warning('Informe um valor maior que zero.');
      return;
    }
    if (!dueDate) {
      toast.warning('Informe a data de vencimento.');
      return;
    }
    if (paid && !paidDate) {
      toast.warning('Informe a data do pagamento.');
      return;
    }

    const effectiveBillable = canMarkBillable && billable;
    // Uma despesa cobrável é ancorada no orçamento DONO, não no que estiver
    // selecionado no combo (que fica travado justamente por isso).
    const effectiveQuoteId = effectiveBillable ? billableQuoteId : quoteId || null;

    const base: FinanceEntryInput = {
      kind,
      description: description.trim(),
      amount: parsedAmount,
      due_date: dueDate,
      status: paid ? 'paid' : 'pending',
      paid_date: paid ? paidDate : null,
      payment_method: paymentMethod || null,
      category_id: categoryId || null,
      client_id: clientId || null,
      quote_id: effectiveQuoteId,
      notes: notes.trim() || null,
      billable: effectiveBillable,
      attachment_url: attachmentUrl,
    };

    const companyId = activeCompany?.id ?? null;
    // O orçamento a sincronizar: o de agora OU o que a despesa tinha antes (o
    // caso "desmarquei cobrável" precisa remover a linha do documento antigo).
    const quoteToSync = effectiveQuoteId ?? (entry?.billable ? entry.quote_id : null);

    try {
      // Bloqueio ANTES de gravar: recusar depois deixaria o financeiro com uma
      // despesa cobrável que nenhum documento cobra. E vincular exige rede — a
      // fila offline não cria a linha nem recalcula `quotes.total`.
      if (billableTouched && quoteToSync) {
        if (!companyId) throw new Error('Nenhuma empresa ativa');
        if (!(await checkNetworkStatus())) throw billableOfflineError();
        await assertQuoteEditableForBillable(quoteToSync, companyId);
      }

      let entryId = entry?.id ?? null;
      if (entry) {
        await updateEntry.mutateAsync({ id: entry.id, updates: base });
      } else {
        const created = await createEntry.mutateAsync({
          ...base,
          recurrence,
          company_id: companyId,
        });
        entryId = created && 'id' in created ? created.id : null;
      }

      if (billableTouched && quoteToSync && entryId && companyId) {
        await syncBillableQuoteLine({
          entryId,
          quoteId: quoteToSync,
          companyId,
          billable: effectiveBillable,
          description: base.description,
          amount: base.amount,
        });
        // O total do documento mudou POR FORA do formulário do orçamento: sem
        // este aviso a tela dele continuaria mostrando o total antigo.
        notifyBillableExpensesUpdated();
        window.dispatchEvent(new CustomEvent('quotes-updated'));
      }

      toast.success(
        entry
          ? 'Lançamento atualizado.'
          : recurrence !== 'none'
            ? 'Lançamentos recorrentes criados.'
            : 'Lançamento criado.',
      );
      close();
    } catch (error) {
      // As mensagens da despesa cobrável já vêm prontas para o usuário; as
      // demais falhas já têm toast dentro das mutations.
      if (error instanceof BillableExpenseError) toast.warning(error.message);
    }
  };

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* O lançamento é um DOCUMENTO com valor, então o valor ao vivo é o VALOR  */
  /* (nos cadastros é o nome). O par Entrada/Saída sobe para cá como pílula: */
  /* é um seletor de duas opções, a mesma natureza do status do orçamento —  */
  /* e era o primeiro controle do formulário, disputando espaço com os       */
  /* campos que a pessoa veio preencher.                                     */
  const heroAmount = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount ?? 0);

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={close}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEdit ? 'Editando' : 'Novo lançamento'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">
            {kind === 'income' ? 'Entrada' : 'Saída'}
          </h1>
        </div>
      </div>

      <div className="relative mt-5">
        <p className="text-xs text-white/75">
          {kind === 'income' ? 'Valor a receber' : 'Valor a pagar'}
        </p>
        <p className={cn('truncate font-bold leading-tight', heroTotalSizeClass(heroAmount))}>
          {heroAmount}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {description.trim() || 'Preencha os dados abaixo'}
        </p>
      </div>

      {/* Entrada × Saída — duas opções, grade de 2 colunas. O pontinho usa a
          cor semântica de cada lado (o resto do app já lê verde/vermelho
          assim), e não a cor da pílula. */}
      <div className="mt-4 grid grid-cols-2 gap-1.5">
        {([
          { value: 'income' as FinanceKind, label: 'Entrada', dot: 'bg-emerald-400' },
          { value: 'expense' as FinanceKind, label: 'Saída', dot: 'bg-red-400' },
        ]).map((option) => {
          const active = option.value === kind;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setKind(option.value)}
              className={cn(
                'flex min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-xs font-medium transition-all',
                active
                  ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                  : 'bg-white/15 text-white/85 hover:bg-white/25',
              )}
            >
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />
              <span className="truncate">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Edição de um lançamento que ainda não chegou (cache frio, link direto).
  if (id && !entry && loadingEntries) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (id && !entry) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">Lançamento não encontrado</h1>
          <Button3D className="mt-6" onClick={() => navigate('/financeiro')}>
            Ver financeiro
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-3">
        <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="fin-desc">Descrição</FieldLabel>
            <Input
              id="fin-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: Pagamento do orçamento #12"
            className={FIELD_INPUT}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <FieldLabel htmlFor="fin-amount">Valor (R$)</FieldLabel>
              {/* O `className` do MoneyInput vai para o WRAPPER, não para o
                  input — então aqui entra layout, não estilo de campo. O
                  `FIELD_INPUT` (`h-10 rounded-xl bg-background`) seria
                  redundante: é exatamente o que o componente já aplica por
                  dentro. `min-w-0` porque item de grade nasce com
                  `min-width: auto` e um valor longo esticaria a coluna (#175). */}
              <MoneyInput id="fin-amount" value={amount} onCommit={setAmount} className="min-w-0" />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="fin-due">Vencimento</FieldLabel>
              <Input
                id="fin-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              className={FIELD_INPUT}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Categoria</Label>
            <Select value={categoryId || 'none'} onValueChange={(v) => setCategoryId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Sem categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem categoria</SelectItem>
                {categoryOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Cliente (opcional)</Label>
            <Select value={clientId || 'none'} onValueChange={(v) => setClientId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Sem cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem cliente</SelectItem>
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Orçamento (opcional)</Label>
            <Select
              value={quoteId || 'none'}
              onValueChange={(v) => handleSelectQuote(v === 'none' ? '' : v)}
              // Despesa cobrável é ancorada no orçamento que a criou: trocar o
              // orçamento aqui moveria a cobrança de documento sem recalcular o
              // total de nenhum dos dois.
              disabled={billable && canMarkBillable}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sem orçamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem orçamento</SelectItem>
                {quotes.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {quoteLabel(q)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Despesa cobrável — só faz sentido numa SAÍDA. Materializa uma
              linha no orçamento (ver src/lib/billableExpense.ts). */}
          {kind === 'expense' && (
            <div className="space-y-1.5 rounded-lg border p-3">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={billable && canMarkBillable}
                  disabled={!canMarkBillable}
                  onChange={(e) => setBillable(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border"
                />
                <span className={canMarkBillable ? '' : 'text-muted-foreground'}>
                  Cobrar do cliente (despesa cobrável)
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                {canMarkBillable ? BILLABLE_HELP_TEXT : BILLABLE_REQUIRES_QUOTE_MESSAGE}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Forma de pagamento</Label>
              <Select
                value={paymentMethod || 'none'}
                onValueChange={(v) => setPaymentMethod(v === 'none' ? '' : (v as PaymentMethod))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label>Recorrência</Label>
                <Select
                  value={recurrence}
                  onValueChange={(v) => setRecurrence(v as Recurrence)}
                  // Ver a nota do efeito: repasse é gasto pontual do documento.
                  disabled={billable && canMarkBillable}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {RECURRENCE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              {kind === 'income' ? 'Já recebido' : 'Já pago'}
            </label>

            {/* Data real do pagamento — pode ser retroativa. É a base do
                Relatório de Pagamentos, então precisa ser do usuário, não "hoje". */}
            {paid && (
              <div className="space-y-1.5">
                <FieldLabel htmlFor="fin-paid-date">
                  {kind === 'income' ? 'Data do recebimento' : 'Data do pagamento'}
                </FieldLabel>
                <Input
                  id="fin-paid-date"
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                className={FIELD_INPUT}
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <FieldLabel htmlFor="fin-notes">Observações (opcional)</FieldLabel>
            <Textarea
              id="fin-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Comprovante anexo. A URL guardada aponta para um bucket PRIVADO —
              a exibição passa por StorageImage, que re-assina (armadilha #6). */}
          <div className="space-y-1.5">
            <FieldLabel htmlFor="fin-attachment">
              Comprovante (opcional)
              {!attachmentFeature.allowed && (
                <span className="ml-2 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">
                  PRO
                </span>
              )}
            </FieldLabel>

            {attachmentUrl ? (
              <div className="flex items-center gap-3 rounded-lg border p-2">
                <StorageImage
                  src={attachmentUrl}
                  alt="Comprovante do lançamento"
                  className="h-14 w-14 rounded object-cover"
                />
                <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {attachmentUrl.startsWith('data:')
                    ? 'Anexado — será enviado ao reconectar.'
                    : 'Comprovante anexado.'}
                </p>
                <Button3D color="gray" size="sm" onClick={() => setAttachmentUrl(null)}>
                  Remover
                </Button3D>
              </div>
            ) : !attachmentFeature.allowed ? (
              // Sem o plano, abrir o seletor de arquivos para só depois mostrar
              // o paywall faz o usuário escolher uma foto à toa. O botão leva
              // direto ao upsell.
              <Button3D color="amber" size="sm" onClick={() => attachmentFeature.require()}>
                Anexar comprovante (PRO)
              </Button3D>
            ) : (
              <Input
                id="fin-attachment"
                type="file"
                // Só imagem: o comprovante é exibido inline (StorageImage) e o
                // caminho offline converte para dataURL de IMAGEM antes de subir
                // no sync — um PDF passaria direto e o dataURL inteiro iria
                // parar dentro da coluna de texto.
                accept="image/*"
                disabled={attachment.isUploading}
                onChange={handleAttachmentChange}
              className={FIELD_INPUT}
              />
            )}
          </div>

          {/* Gerar recibo — só para uma ENTRADA já recebida (comprovante de
              pagamento). Nunca cria automático: abre o formulário pré-preenchido. */}
          {isEdit && entry && kind === 'income' && paid && (
            <div className="rounded-lg border p-3">
              <p className="mb-2 text-xs text-muted-foreground">
                Este recebimento já foi pago. Gere o recibo para o cliente.
              </p>
              <GenerateReceiptButton
                className="w-full"
                prefill={{
                  finance_entry_id: entry.id,
                  client_id: clientId || null,
                  quote_id: quoteId || null,
                  payer_name: clients.find((c: any) => c.id === clientId)?.name ?? null,
                  amount: amount || Number(entry.amount ?? 0),
                  description: description.trim() || null,
                  payment_method: paymentMethod || null,
                }}
              />
            </div>
          )}
        </div>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página. */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D color="gray" size="sm" className="flex-1" onClick={close} disabled={submitting}>
              Cancelar
            </Button3D>
            <Button3D size="sm" className="flex-1" onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEdit ? 'Salvar' : 'Adicionar'}
            </Button3D>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
